import { UploadProps, VideoUploadStatus } from "store/uploads/types";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { startPollingUpload } from "store/uploads/thunks";
import { AppDispatch } from "store/store";

/**
 * Monitor the state of processing uploads.
 */
export const useVideoUpload = (
    broadcastId: string,
    videoStatus: VideoUploadStatus
) => {
    const { processingOrQueued, ready } = useSelector(
        (state: RootState) => state.uploads
    );
    const dispatch = useDispatch<AppDispatch>();

    const pollUpload = useCallback(
        async (uploadProps: UploadProps) => {
            dispatch(startPollingUpload(uploadProps));
        },
        [dispatch]
    );

    const getUploadStatus = useCallback(
        (uploadProps: UploadProps) => {
            const isInProcessing = !!processingOrQueued?.find(
                (ul) => ul?.broadcastId === uploadProps?.broadcastId
            );

            const isInReady = !!ready?.find(
                (ul) => ul?.broadcastId === uploadProps?.broadcastId
            );

            if (isInProcessing) return VideoUploadStatus.Processing;

            if (isInReady) return VideoUploadStatus.Success;
        },
        [processingOrQueued, ready]
    );

    /** If a broadcast has a video in a pending state, add it to a list of broadcasts to poll. */
    useEffect(() => {
        if (!broadcastId) return;

        if (
            videoStatus === VideoUploadStatus.Processing ||
            videoStatus === VideoUploadStatus.Queued
        ) {
            pollUpload({
                broadcastId
            });
        }
    }, [broadcastId, videoStatus, pollUpload]);

    return {
        /** Ping Cloudflare with an API call every ten seconds to get upload status */
        pollUpload,
        /** Initiate polling to ping Cloudflare with an API call every ten seconds to get upload status */
        getUploadStatus,
        /** List of broadcasts that are already uploaded. Broadcasts are added to this state after polling. */
        ready
    };
};
